import React, { useState } from "react";
import { motion } from "framer-motion";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    Cell,
    PieChart,
    Pie,
    Label,
    LabelList,
} from "recharts";
import {
    MapPin,
    ShieldCheck,
    BarChart3,
    Users,
    Building2,
    BriefcaseBusiness,
    DollarSign,
} from "lucide-react";

const BAR_COLORS = ["#2563eb", "#0f766e", "#0891b2", "#7c3aed", "#f59e0b", "#ef4444", "#14b8a6", "#6366f1", "#84cc16", "#f97316"];
const PIE_COLORS = ["#1d4ed8", "#0f766e", "#7c3aed", "#f59e0b", "#ef4444", "#14b8a6"];

const parseRevenueToMillions = (value) => {
    if (!value) return 0;

    const match = String(value).match(/([\d,.]+)\s*(Billion|Million)/i);
    if (!match) return 0;

    const amount = Number(match[1].replace(/,/g, ""));
    return match[2].toLowerCase() === "billion" ? amount * 1000 : amount;
};

const parseEmployeeEstimate = (value) => {
    if (!value) return 0;

    const normalized = String(value).replace(/,/g, "");
    const rangeMatch = normalized.match(/(\d+)\s*-\s*(\d+)/);
    if (rangeMatch) {
        return (Number(rangeMatch[1]) + Number(rangeMatch[2])) / 2;
    }

    const plusMatch = normalized.match(/(\d+)\+/);
    if (plusMatch) {
        return Number(plusMatch[1]);
    }

    return Number(normalized) || 0;
};

const shortCompanyName = (name = "") =>
    name
        .replace("Construction", "Constr.")
        .replace("Companies", "Cos.")
        .replace("Company", "Co.")
        .replace("Corporation", "Corp.")
        .replace("Products", "Prod.");

const formatRevenue = (valueInMillions) => {
    if (valueInMillions >= 1000) {
        return `$${(valueInMillions / 1000).toFixed(valueInMillions % 1000 === 0 ? 0 : 1)}B`;
    }

    return `$${valueInMillions.toFixed(valueInMillions >= 100 ? 0 : 1)}M`;
};

const formatEmployees = (value) => {
    if (value >= 1000) {
        return `${(value / 1000).toFixed(value >= 10000 ? 0 : 1)}k`;
    }

    return `${Math.round(value)}`;
};

const ChartTooltip = ({ active, payload, formatter }) => {
    if (!active || !payload || !payload.length) return null;

    const row = payload[0].payload;

    return (
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-xl">
            <div className="text-xs font-black text-slate-900 mb-1">{row.Company || row.name}</div>
            <div className="text-sm font-bold text-slate-800">{formatter(payload[0].value, row)}</div>
        </div>
    );
};

const TopCompanies = ({ data }) => {
    const [viewMode, setViewMode] = useState("charts");

    if (!data || data.length === 0) return null;

    const companies = data.map((company, index) => ({
        ...company,
        rank: index + 1,
        shortName: shortCompanyName(company.Company),
        revenueMillions: parseRevenueToMillions(company["Estimated Annual Revenue"]),
        employeeEstimate: parseEmployeeEstimate(company["Estimated Employee Count"]),
        city: String(company.Headquarters || "").split(",")[0].trim(),
    }));

    const revenueData = [...companies].sort((a, b) => b.revenueMillions - a.revenueMillions);
    const workforceData = [...companies].sort((a, b) => b.employeeEstimate - a.employeeEstimate);

    const headquartersMap = companies.reduce((acc, company) => {
        acc[company.city] = (acc[company.city] || 0) + 1;
        return acc;
    }, {});

    const headquartersData = Object.entries(headquartersMap)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value);

    const industryMap = companies.reduce((acc, company) => {
        String(company.Industry || "")
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean)
            .forEach((industry) => {
                acc[industry] = (acc[industry] || 0) + 1;
            });
        return acc;
    }, {});

    const leadingIndustry = Object.entries(industryMap).sort((a, b) => b[1] - a[1])[0];
    const totalRevenue = companies.reduce((sum, company) => sum + company.revenueMillions, 0);
    const totalEmployees = companies.reduce((sum, company) => sum + company.employeeEstimate, 0);
    const topCity = headquartersData[0];

    return (
        <div className="mb-6 max-w-[1400px] mx-auto px-2 sm:px-3">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-4">
                    <div className="p-3 bg-blue-600 rounded-2xl text-white shadow-lg shadow-blue-100">
                        <ShieldCheck size={24} />
                    </div>
                    <div>
                        <h2 className="text-2xl md:text-3xl font-black text-gray-900 tracking-tighter leading-none mb-1">
                            Utah's <span className="text-blue-600">Construction</span> Titans
                        </h2>
                        <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">
                            Top 10 Industry Powerhouses
                        </p>
                    </div>
                </div>
                <div className="hidden lg:block text-right">
                    <p className="text-[10px] font-black text-gray-300 uppercase tracking-[0.3em]">
                        Corporate Impact Index | Q1 2026
                    </p>
                </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <p className="text-sm text-slate-500 mb-0">
                    Switch between visual charts and raw company metrics.
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        type="button"
                        onClick={() => setViewMode("charts")}
                        className={`px-4 py-2 rounded-full border text-sm font-semibold transition-all ${
                            viewMode === "charts"
                                ? "border-blue-200 bg-blue-50 text-blue-700"
                                : "border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-700"
                        }`}
                        aria-pressed={viewMode === "charts"}
                    >
                        View charts
                    </button>
                    <button
                        type="button"
                        onClick={() => setViewMode("metrics")}
                        className={`px-4 py-2 rounded-full border text-sm font-semibold transition-all ${
                            viewMode === "metrics"
                                ? "border-blue-200 bg-blue-50 text-blue-700"
                                : "border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-700"
                        }`}
                        aria-pressed={viewMode === "metrics"}
                    >
                        View metric values
                    </button>
                </div>
            </div>

            {viewMode === "charts" && (
                <>
            <div className="grid grid-cols-1 xl:grid-cols-[1.55fr_1fr] gap-4 mb-3">
                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="rounded-[1.75rem] border border-slate-200 bg-white p-4 pb-3 shadow-sm"
                >
                    <div className="flex items-center justify-between gap-4 mb-5">
                        <div className="flex items-center gap-3">
                            <div className="rounded-2xl bg-blue-50 p-3 text-blue-600">
                                <BarChart3 size={20} />
                            </div>
                            <div>
                                <h3 className="text-lg font-black text-slate-900 mb-1">Revenue Leaderboard</h3>
                                <p className="text-sm text-slate-500 mb-0">Estimated annual revenue by company.</p>
                            </div>
                        </div>
                        <div className="rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1 text-[10px] font-black uppercase tracking-[0.25em] text-emerald-700">
                            Dollars
                        </div>
                    </div>

                    <div style={{ height: 330, minWidth: 0 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={revenueData} layout="vertical" margin={{ top: 6, right: 58, left: 10, bottom: 22 }}>
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                                <XAxis
                                    type="number"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fontSize: 11, fill: "#64748b" }}
                                    tickFormatter={(value) => formatRevenue(value)}
                                >
                                    <Label
                                        value="Estimated Annual Revenue"
                                        offset={-2}
                                        position="insideBottom"
                                        style={{ fontSize: 11, fill: "#94a3b8", fontWeight: 700 }}
                                    />
                                </XAxis>
                                <YAxis
                                    type="category"
                                    dataKey="shortName"
                                    width={116}
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fontSize: 11, fill: "#0f172a", fontWeight: 700 }}
                                >
                                    <Label
                                        value="Companies"
                                        angle={-90}
                                        position="insideLeft"
                                        style={{ textAnchor: "middle", fontSize: 11, fill: "#94a3b8", fontWeight: 700 }}
                                    />
                                </YAxis>
                                <Tooltip
                                    cursor={{ fill: "rgba(239, 246, 255, 0.9)" }}
                                    content={
                                        <ChartTooltip
                                            formatter={(value) => `${formatRevenue(value)} estimated annual revenue`}
                                        />
                                    }
                                />
                                <Bar dataKey="revenueMillions" radius={[0, 10, 10, 0]} barSize={22}>
                                    <LabelList
                                        dataKey="revenueMillions"
                                        position="right"
                                        offset={8}
                                        formatter={(value) => formatRevenue(value)}
                                        style={{ fontSize: 10, fill: "#0f172a", fontWeight: 700 }}
                                    />
                                    {revenueData.map((entry, index) => (
                                        <Cell key={entry.Company} fill={BAR_COLORS[index % BAR_COLORS.length]} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                </motion.div>

                <div className="grid grid-cols-1 gap-4">
                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="rounded-[1.75rem] border border-slate-200 bg-white p-4 shadow-sm"
                    >
                        <div className="flex items-center justify-between gap-4 mb-5">
                            <div className="flex items-center gap-3">
                                <div className="rounded-2xl bg-cyan-50 p-3 text-cyan-700">
                                    <Users size={20} />
                                </div>
                                <div>
                                    <h3 className="text-lg font-black text-slate-900 mb-1">Workforce Footprint</h3>
                                    <p className="text-sm text-slate-500 mb-0">Employee ranges converted into chartable estimates.</p>
                                </div>
                            </div>
                        </div>

                        <div style={{ height: 304, minWidth: 0 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={workforceData} layout="vertical" margin={{ top: 6, right: 52, left: 10, bottom: 22 }}>
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                                    <XAxis
                                        type="number"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fontSize: 11, fill: "#64748b" }}
                                        tickFormatter={(value) => formatEmployees(value)}
                                    >
                                        <Label
                                            value="Estimated Employee Count"
                                            offset={-2}
                                            position="insideBottom"
                                            style={{ fontSize: 11, fill: "#94a3b8", fontWeight: 700 }}
                                        />
                                    </XAxis>
                                    <YAxis
                                        type="category"
                                        dataKey="shortName"
                                        width={100}
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fontSize: 10, fill: "#0f172a", fontWeight: 700 }}
                                    >
                                        <Label
                                            value="Companies"
                                            angle={-90}
                                            position="insideLeft"
                                            style={{ textAnchor: "middle", fontSize: 11, fill: "#94a3b8", fontWeight: 700 }}
                                        />
                                    </YAxis>
                                    <Tooltip
                                        cursor={{ fill: "rgba(236, 254, 255, 0.9)" }}
                                        content={
                                            <ChartTooltip
                                                formatter={(value, row) => `${formatEmployees(value)} plotted estimate (${row["Estimated Employee Count"]} reported)`}
                                            />
                                        }
                                    />
                                    <Bar dataKey="employeeEstimate" radius={[0, 10, 10, 0]} fill="#0891b2" barSize={18}>
                                        <LabelList
                                            dataKey="employeeEstimate"
                                            position="right"
                                            offset={8}
                                            formatter={(value) => formatEmployees(value)}
                                            style={{ fontSize: 10, fill: "#0f172a", fontWeight: 700 }}
                                        />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        <p className="mt-3 mb-0 text-[11px] font-medium text-slate-500">
                            Ranges use midpoints when given as intervals and lower bounds when shown as "plus" values.
                        </p>
                    </motion.div>
                </div>
            </div>

            <motion.div
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="rounded-[1.75rem] border border-slate-200 bg-white p-4 shadow-sm mb-4"
            >
                <div className="flex items-center justify-between gap-4 mb-3">
                    <div className="flex items-center gap-3">
                        <div className="rounded-2xl bg-indigo-50 p-3 text-indigo-700">
                            <MapPin size={20} />
                        </div>
                        <div>
                            <h3 className="text-lg font-black text-slate-900 mb-1">Headquarters Concentration</h3>
                            <p className="text-sm text-slate-500 mb-0">Where the top firms are anchored across Utah.</p>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-[0.85fr_1.15fr] gap-4 items-center">
                    <div className="relative mx-auto lg:mx-0" style={{ height: 210, width: "100%", maxWidth: 320 }}>
                        <div className="relative z-10 h-full w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={headquartersData}
                                        dataKey="value"
                                        nameKey="name"
                                        innerRadius={64}
                                        outerRadius={104}
                                        paddingAngle={3}
                                    >
                                        {headquartersData.map((entry, index) => (
                                            <Cell key={entry.name} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        wrapperStyle={{ zIndex: 40 }}
                                        content={
                                            <ChartTooltip
                                                formatter={(value, row) => `${value} companies headquartered in ${row.name}`}
                                            />
                                        }
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="absolute inset-0 z-0 flex flex-col items-center justify-center pointer-events-none">
                            <div className="text-2xl font-black text-slate-900">{headquartersData.length}</div>
                            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-400">Cities</div>
                        </div>
                    </div>

                    <div className="space-y-2">
                        {headquartersData.map((entry, index) => (
                            <div key={entry.name} className="rounded-2xl border border-slate-100 bg-slate-50 px-3 py-2">
                                <div className="flex items-center justify-between gap-3 mb-1">
                                    <span className="text-sm font-bold text-slate-800">{entry.name}</span>
                                    <span className="text-xs font-black text-slate-500">{entry.value}</span>
                                </div>
                                <div className="h-2 rounded-full bg-white overflow-hidden">
                                    <div
                                        className="h-full rounded-full"
                                        style={{
                                            width: `${(entry.value / companies.length) * 100}%`,
                                            background: PIE_COLORS[index % PIE_COLORS.length],
                                        }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </motion.div>
                </>
            )}

            {viewMode === "metrics" && (
                <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
                <div className="rounded-[1.5rem] border border-emerald-100 bg-emerald-50/70 p-5">
                    <div className="flex items-center gap-2 text-emerald-700 mb-3">
                        <DollarSign size={18} />
                        <span className="text-[11px] font-black uppercase tracking-[0.25em]">Combined Revenue</span>
                    </div>
                    <div className="text-3xl font-black text-slate-900">{formatRevenue(totalRevenue)}</div>
                    <p className="text-sm text-slate-600 mb-0 mt-2">
                        Estimated annual revenue represented across the top 10 firms.
                    </p>
                </div>

                <div className="rounded-[1.5rem] border border-blue-100 bg-blue-50/70 p-5">
                    <div className="flex items-center gap-2 text-blue-700 mb-3">
                        <Users size={18} />
                        <span className="text-[11px] font-black uppercase tracking-[0.25em]">Estimated Workforce</span>
                    </div>
                    <div className="text-3xl font-black text-slate-900">{formatEmployees(totalEmployees)}</div>
                    <p className="text-sm text-slate-600 mb-0 mt-2">
                        Midpoints and lower-bound employee ranges translated into a visual estimate.
                    </p>
                </div>

                <div className="rounded-[1.5rem] border border-cyan-100 bg-cyan-50/70 p-5">
                    <div className="flex items-center gap-2 text-cyan-700 mb-3">
                        <Building2 size={18} />
                        <span className="text-[11px] font-black uppercase tracking-[0.25em]">HQ Cluster</span>
                    </div>
                    <div className="text-3xl font-black text-slate-900">{topCity?.name || "-"}</div>
                    <p className="text-sm text-slate-600 mb-0 mt-2">
                        {topCity ? `${topCity.value} of 10 companies are headquartered here.` : "Headquarters mix unavailable."}
                    </p>
                </div>

                <div className="rounded-[1.5rem] border border-violet-100 bg-violet-50/70 p-5">
                    <div className="flex items-center gap-2 text-violet-700 mb-3">
                        <BriefcaseBusiness size={18} />
                        <span className="text-[11px] font-black uppercase tracking-[0.25em]">Leading Specialty</span>
                    </div>
                    <div className="text-2xl font-black text-slate-900 leading-tight">{leadingIndustry?.[0] || "-"}</div>
                    <p className="text-sm text-slate-600 mb-0 mt-2">
                        {leadingIndustry ? `${leadingIndustry[1]} companies list this as a core capability.` : "Industry mix unavailable."}
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4">
                {companies.map((company, index) => (
                    <motion.div
                        key={company.Company}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{
                            delay: (index % 5) * 0.05,
                            type: "spring",
                            stiffness: 100,
                            damping: 15,
                        }}
                        whileHover={{
                            x: 8,
                            backgroundColor: "rgba(248, 250, 252, 1)",
                            borderColor: "rgba(59, 130, 246, 0.2)",
                        }}
                        className="group relative bg-white border border-gray-100 rounded-[1.5rem] p-4 transition-all duration-300 flex items-center gap-4 hover:shadow-xl hover:shadow-blue-50/20 active:scale-[0.98]"
                    >
                        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center border border-gray-100 group-hover:bg-blue-600 group-hover:border-blue-600 transition-colors duration-300">
                            <span className="text-sm font-black text-gray-400 group-hover:text-white transition-colors">
                                {String(index + 1).padStart(2, "0")}
                            </span>
                        </div>

                        <div className="flex-grow min-w-0">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 mb-1">
                                <h3 className="text-sm md:text-base font-black text-gray-900 group-hover:text-blue-600 transition-colors leading-tight">
                                    {company.Company}
                                </h3>
                                <div className="flex items-center gap-1.5 px-2 py-0.5 bg-gray-50 rounded-full text-[9px] font-bold text-gray-400 group-hover:bg-blue-50 group-hover:text-blue-500 transition-colors whitespace-nowrap">
                                    <MapPin size={10} />
                                    {company.Headquarters}
                                </div>
                            </div>

                            <p className="text-[10px] text-gray-400 font-medium opacity-60 group-hover:opacity-100 transition-opacity leading-relaxed">
                                {company.Industry}
                            </p>
                        </div>

                        <div className="flex items-center gap-4 md:gap-6 flex-shrink-0 ml-2">
                            <div className="text-right">
                                <div className="text-[8px] font-black text-emerald-500 uppercase tracking-widest mb-0.5">Revenue</div>
                                <div className="text-xs md:text-sm font-black text-gray-700 tracking-tighter tabular-nums">
                                    {company["Estimated Annual Revenue"]}
                                </div>
                            </div>

                            <div className="text-right border-l border-gray-100 pl-4">
                                <div className="text-[8px] font-black text-blue-500 uppercase tracking-widest mb-0.5">Force</div>
                                <div className="text-xs md:text-sm font-black text-gray-700 tracking-tighter tabular-nums">
                                    {company["Estimated Employee Count"]}
                                </div>
                            </div>
                        </div>

                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-blue-600 rounded-r-full opacity-0 group-hover:opacity-100 transition-opacity" />
                    </motion.div>
                ))}
            </div>
                </>
            )}

            <div className="mt-5 flex flex-wrap items-center justify-center gap-6">
                <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    <span className="text-[9px] font-bold text-gray-300 uppercase tracking-widest">Revenue Integrity</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                    <span className="text-[9px] font-bold text-gray-300 uppercase tracking-widest">Verified Workforce</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                    <span className="text-[9px] font-bold text-gray-300 uppercase tracking-widest">HQ Density</span>
                </div>
            </div>
        </div>
    );
};

export default TopCompanies;
